import React from 'react'
import { Copy, X } from 'lucide-react'
import type { OpportunitySignals } from '../services/signalsService'
import { Button, Tooltip } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'

type Props = {
  signals: OpportunitySignals
  onClose?: () => void
}

export function SignalCopyPanel({ signals, onClose }: Props) {
  const { add: toast } = useToast()

  const {
    side,
    entryMin,
    entryMax,
    entryMid,
    tp1,
    tp2,
    tp3,
    stopPrice,
    stopAggressive,
    stopConservative,
  } = signals

  const effectiveStop =
    stopAggressive != null
      ? stopAggressive
      : stopConservative != null
      ? stopConservative
      : stopPrice ?? null

  const fmt = (v: number | null | undefined) =>
    v != null ? v.toFixed(6) : '-'

  const baseEntryMid =
    entryMid ??
    (entryMin != null && entryMax != null
      ? (entryMin + entryMax) / 2
      : null)

  const fmtPct = (value: number | null | undefined): string | null => {
    if (baseEntryMid == null || value == null) return null
    const pct = Math.abs(((value - baseEntryMid) / baseEntryMid) * 100)
    if (!Number.isFinite(pct)) return null
    return `${pct.toFixed(2)}%`
  }

  const entry1Text = entryMin != null ? fmt(entryMin) : null
  const entry2Text = entryMax != null ? fmt(entryMax) : null
  const stopText =
    effectiveStop != null
      ? fmt(effectiveStop)
      : null

  const alvo1Text =
    tp1 != null ? fmt(tp1) : null
  const alvo2Text =
    tp2 != null ? fmt(tp2) : null
  const alvo3Text =
    tp3 != null ? fmt(tp3) : null

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast({
        title: 'Copiado',
        description: 'Dados copiados para a área de transferência.',
        variant: 'success',
        soft: true,
      })
    } catch {
      toast({
        title: 'Falha ao copiar',
        description: 'Não foi possível copiar para a área de transferência.',
        variant: 'error',
        soft: true,
      })
    }
  }

  const title =
    side === 'long'
      ? 'Copiar dados da operação Long'
      : side === 'short'
      ? 'Copiar dados da operação Short'
      : 'Copiar dados da oportunidade'

  const renderCell = (label: string, value: string | null) => {
    const lower = label.toLowerCase()
    const isStop = lower.includes('stop')
    const isTarget = lower.includes('alvo')

    const valueColorClass = isStop
      ? 'text-red-400'
      : isTarget
      ? 'text-emerald-400'
      : 'text-foreground'

    return (
      <div className="flex flex-col rounded-md bg-[#161616] px-2 py-1.5">
        <span className="mb-0.5 text-[9px] font-medium text-foreground/70">
          {label}
        </span>
        <div className="flex items-center justify-between gap-1">
          <span
            className={[
              'break-all text-[11px] font-semibold sm:text-[12px]',
              valueColorClass,
            ].join(' ')}
          >
            {value ?? '-'}
          </span>
          <Tooltip content="Copiar">
          <Button
            type="button"
            variant="ghost"
            className="h-7 w-7 p-0"
            disabled={!value}
            onClick={() => {
              if (!value) return
              handleCopy(value)
            }}
          >
              <Copy className="h-3.5 w-3.5" />
            </Button>
          </Tooltip>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="flex-1 text-center text-[12px] font-semibold text-foreground">
          {title}
        </div>
        {onClose && (
          <Button
            type="button"
            variant="ghost"
            className="h-7 w-7 flex-shrink-0 p-0"
            onClick={onClose}
            aria-label="Fechar"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="space-y-2">
        {/* Entradas + Stop */}
        <div className="grid grid-cols-3 gap-2">
          {renderCell('Entrada 1', entry1Text)}
          {renderCell('Entrada 2', entry2Text)}
          {renderCell('Stop', stopText)}
        </div>

        {/* Alvos */}
        <div className="grid grid-cols-3 gap-2">
          {renderCell('Alvo 1', alvo1Text)}
          {renderCell('Alvo 2', alvo2Text)}
          {renderCell('Alvo 3', alvo3Text)}
        </div>
      </div>
    </div>
  )
}
